import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardActionOverview, PaymentCardApiService } from '@client-monorepo/payment/card-data';
import { AllCardsModel } from '../../data-access/models/all-cards.model';
import { CardCtaItems, CardsCtaItemsModel } from '../../data-access/models/cards-cta-items.model';
import { CardActionMapper, CardCtaTypes, CardTitlesItemsMap, CtaCardsOrderMapper } from '../../data-access/constants/card-cta-types.const';
import { CardsSliderComponent } from '../cards-slider/cards-slider.component';
import { CardsCtaComponent } from '../cards-cta/cards-cta.component';
import { CardCtaItemMapper, CardCtaItemType } from '../../data-access/constants/card-cta-items.const';
import { MessageService, rangeCreator } from '@client-monorepo/common/utilities';
import { NgxSkeletonLoadingComponent } from '@digipay/ngx-skeleton-loading';
import { Action, ActionHandlerService, ActionType, RedirectPayload } from '@client-monorepo/common/action-handler';
import { AllCardsArrayModel } from '../../data-access/models/all-cards-array.model';
import { CardActionButton } from '../../../../../../shared/payment/card-data/src/lib/data-access/models/card-action-button.interface';
import { NgxCarouselComponent, NgxCarouselSlideDirective } from '@digipay/ngx-carousel';
import { NgxAppBarButtonType, NgxAppBarComponent } from '@digipay/ngx-app-bar';

@Component({
  selector: 'transactions-applet-card-actions',
  standalone: true,
  imports: [
    CommonModule,
    CardsSliderComponent,
    CardsCtaComponent,
    NgxSkeletonLoadingComponent,
    NgxCarouselComponent,
    NgxCarouselSlideDirective,
    NgxAppBarComponent,
  ],
  templateUrl: './card-actions.component.html',
  styleUrl: './card-actions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardActionsComponent implements OnInit {
  // Injections
  messageService = inject(MessageService);
  paymentCardApiService = inject(PaymentCardApiService);
  actionHandler = inject(ActionHandlerService);

  // Variables
  readonly rangeCreator = rangeCreator;
  readonly cardTitlesItemsMap = CardTitlesItemsMap;
  allCards = signal<AllCardsModel>({} as AllCardsModel);
  allCardsArray = computed(() => this.convertAllCardsToKeyValueArray(this.allCards()));
  isCardsCarouselVisible = signal<boolean>(false);
  cardCtaItems = signal<CardsCtaItemsModel>({} as CardsCtaItemsModel);
  carouselActiveIndex = signal(0);
  cardsActiveIndex = signal(0);
  titleNames = computed(() => this.titleGenerator());
  isLoading = signal<boolean>(true);
  isCarouselSwiping = signal<boolean>(false);
  isCardsAnimating = signal<boolean>(false);

  rightButton = computed(() => {
    const button: NgxAppBarButtonType = {};
    if (this.carouselActiveIndex() !== 0) {
      button.icon = 'arrow-2-right';
      button.size = 'small';
      button.style = 'link';
      button.label = this.titleNames()?.before;
    }
    return Object.keys(button)?.length ? button : null;
  });
  leftButton = computed(() => {
    const button: NgxAppBarButtonType = {};
    if (this.carouselActiveIndex() < this.allCardsArray().length - 1) {
      button.icon = 'arrow-2-left';
      button.size = 'small';
      button.style = 'link';
      button.label = this.titleNames()?.after;
    }
    return Object.keys(button)?.length ? button : null;
  });

  constructor() {
    effect(
      () => {
        if (Object.keys(this.allCards()).length > 0) {
          this.handleCarouselVisibility();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.getSavedBankCards();
    this.getCreditWallets();
    this.getBNPLWallets();
    this.getDebitWallets();
  }

  getSavedBankCards(): void {
    this.paymentCardApiService.getSavedBankCards().subscribe({
      next: (res) => {
        const data: CardActionOverview[] = res.data;
        if (res.data.length === 0) {
          data.push({
            card: {
              logoImageId: undefined,
              imageId: undefined,
              alias: {
                value: '',
                textColor: undefined,
                backgroundColor: undefined,
                imageId: '',
              },
              mainValue: {
                value: '',
                textColor: undefined,
                backgroundColor: undefined,
                imageId: '',
              },
              color: [],
              leftLabel: {
                value: '',
                textColor: undefined,
                backgroundColor: undefined,
                imageId: '',
              },
              rightLabel: {
                value: '',
                textColor: undefined,
                backgroundColor: undefined,
                imageId: '',
              },
              featureName: '',
            },
            actionBar: {},
          });
        }
        this.allCards.set({
          ...this.allCards(),
          [CardCtaTypes.BANK_CARDS]: data,
        });
        this.isLoading.set(false);
      },
      error: (error) => {
        this.allCards.set({
          ...this.allCards(),
          [CardCtaTypes.BANK_CARDS]: [],
        });
        this.messageService.showErrorOfErrorResponse(error);
      },
    });
  }

  getDebitWallets(): void {
    this.paymentCardApiService.getDebitWallets().subscribe({
      next: (res) => {
        this.allCards.set({
          ...this.allCards(),
          [CardCtaTypes.WALLET]: res.data,
        });
      },
      error: (error) => {
        this.allCards.set({
          ...this.allCards(),
          [CardCtaTypes.WALLET]: [],
        });
        this.messageService.showErrorOfErrorResponse(error);
      },
    });
  }

  getBNPLWallets(): void {
    this.paymentCardApiService.getBnplWallets().subscribe({
      next: (res) => {
        this.allCards.set({
          ...this.allCards(),
          [CardCtaTypes.BNPL]: res.data,
        });
      },
      error: (error) => {
        this.allCards.set({
          ...this.allCards(),
          [CardCtaTypes.BNPL]: [],
        });
        this.messageService.showErrorOfErrorResponse(error);
      },
    });
  }

  getCreditWallets(): void {
    this.paymentCardApiService.getCreditWallets().subscribe({
      next: (res) => {
        this.allCards.set({
          ...this.allCards(),
          [CardCtaTypes.CREDIT]: res.data,
        });
      },
      error: (error) => {
        this.allCards.set({
          ...this.allCards(),
          [CardCtaTypes.CREDIT]: [],
        });
        this.messageService.showErrorOfErrorResponse(error);
      },
    });
  }

  handleCarouselVisibility(): void {
    let isVisible = true;
    for (const key of Object.values(CardCtaTypes)) {
      isVisible = isVisible && !!this.allCards()[key];
    }
    if (isVisible !== this.isCardsCarouselVisible()) {
      if (isVisible) {
        this.convertAllCardsToKeyValueArray(this.allCards());
        this.changeCtaItems(
          this.allCardsArray()[this.carouselActiveIndex()]?.cardsType,
          this.allCardsArray()[this.carouselActiveIndex()]?.cards[0],
        );
      }
      this.isCardsCarouselVisible.set(isVisible);
    }
  }

  convertAllCardsToKeyValueArray(input: AllCardsModel): AllCardsArrayModel[] {
    return Object.keys(input)
      .filter((key) => input[key as CardCtaTypes]?.length > 0)
      .map((key) => {
        const type = CardCtaTypes[key.toUpperCase() as keyof typeof CardCtaTypes];
        return {
          cardsType: type,
          cards: this.addDefaultActionsToCards(input[key as CardCtaTypes], type),
          order: CtaCardsOrderMapper[type],
        };
      })
      .sort((a, b) => a.order - b.order);
  }

  addDefaultActionsToCards(cards: CardActionOverview[], type: CardCtaTypes): CardActionOverview[] {
    return cards.map((card) => {
      if (type === CardCtaTypes.BANK_CARDS && cards.length === 1 && card.card.mainValue.value === '') {
        return {
          ...card,
          defaultAction: {
            type: ActionType.REDIRECT,
            payload: {
              url: 'profile/saved-cards',
            },
          },
        };
      }
      return {
        ...card,
        defaultAction: CardActionMapper[type],
      };
    });
  }

  titleGenerator(): { before: string; now: string; after: string } {
    return {
      before:
        this.carouselActiveIndex() === 0 ? '' : this.cardTitlesItemsMap[this.allCardsArray()[this.carouselActiveIndex() - 1]?.cardsType],
      now: this.cardTitlesItemsMap[this.allCardsArray()[this.carouselActiveIndex()]?.cardsType],
      after:
        this.carouselActiveIndex() === this.allCardsArray.length - 1
          ? ''
          : this.cardTitlesItemsMap[this.allCardsArray()[this.carouselActiveIndex() + 1]?.cardsType],
    };
  }

  handleCarouselSwiping(isRunning: boolean): void {
    this.isCarouselSwiping.set(isRunning);
  }

  handleCardsAnimationRunning(isRunning: boolean): void {
    this.isCardsAnimating.set(isRunning);
  }

  handleCardClick(card: CardActionOverview, item: AllCardsArrayModel): void {
    if (this.isCarouselSwiping() || this.isCardsAnimating()) {
      return;
    }
    if (this.allCardsArray().indexOf(item) !== this.carouselActiveIndex()) {
      this.carouselActiveIndex.set(this.allCardsArray().indexOf(item));
      this.handleCarouselActiveIndexChange(this.carouselActiveIndex());
    } else {
      if (card && card.defaultAction) {
        this.addCloseServiceUrlToCard(card);
        this.actionHandler.handle(card.defaultAction);
      }
    }
  }

  addCloseServiceUrlToCard(card: CardActionOverview) {
    if (card.defaultAction && card.defaultAction.type === ActionType.GO_TO_SERVICE) {
      card.defaultAction.payload.closeServiceUrl = window.location.pathname;
    }
  }

  isCardSliderCollapsed(item: AllCardsArrayModel): boolean {
    return this.allCardsArray().indexOf(item) !== this.carouselActiveIndex();
  }

  handleTitleBtnClick(direction: 'next' | 'prev') {
    if (direction === 'next') {
      this.carouselActiveIndex.update((v) => v + 1);
    } else if (direction === 'prev') {
      this.carouselActiveIndex.update((v) => v - 1);
    }
    this.handleCarouselActiveIndexChange(this.carouselActiveIndex());
  }

  handleCarouselActiveIndexChange(index: number): void {
    this.carouselActiveIndex.set(index);
    this.changeCtaItems(this.allCardsArray()[index]?.cardsType, this.allCardsArray()[index]?.cards[0]);
  }

  handleCardsActiveIndexChange(index: number): void {
    this.cardsActiveIndex.set(index);
    this.changeCtaItems(
      this.allCardsArray()[this.carouselActiveIndex()]?.cardsType,
      this.allCardsArray()[this.carouselActiveIndex()]?.cards[index],
    );
  }

  changeCtaItems(cardType: CardCtaTypes, card: CardActionOverview): void {
    if (!cardType) {
      return;
    }
    const actionsArray: CardActionButton[] = this.extractFromObject(card?.actionBar);
    let defaultCtaArray: CardCtaItemType[] = [];
    let fromServerPart: CardCtaItemType[] = [];
    const arrayToBe: CardCtaItems[] = [];
    switch (cardType) {
      case CardCtaTypes.BANK_CARDS:
        defaultCtaArray = [CardCtaItemType.C2C, CardCtaItemType.ADD_CARD, CardCtaItemType.CONNECT_CARD_TO_CREDIT, CardCtaItemType.MY_CARDS];
        break;
      case CardCtaTypes.BNPL:
        defaultCtaArray = [CardCtaItemType.BNPL_INSTALLMENT, CardCtaItemType.STORES, CardCtaItemType.BARCODE_BUY, CardCtaItemType.QR];
        break;
      case CardCtaTypes.CREDIT:
        defaultCtaArray = [CardCtaItemType.INSTALLMENT, CardCtaItemType.STORES, CardCtaItemType.BARCODE_BUY, CardCtaItemType.QR];
        break;
      case CardCtaTypes.WALLET:
        defaultCtaArray = [
          CardCtaItemType.CASH_IN,
          CardCtaItemType.CASH_OUT,
          CardCtaItemType.ADD_GIFT_CARD,
          CardCtaItemType.QR,
          CardCtaItemType.TRANSFER,
        ];
        break;
    }
    fromServerPart = defaultCtaArray.slice(0, actionsArray.length);
    fromServerPart.forEach((type: CardCtaItemType, index) => {
      const currentStatic: CardCtaItems = CardCtaItemMapper[type];
      if (cardType === CardCtaTypes.CREDIT) {
        const creditId: string = actionsArray[index]?.payload?.creditId ? actionsArray[index]?.payload?.creditId : '';
        const url = 'service/credit/go-to-wallet/' + creditId;
        arrayToBe.push(
          this.manipulateCtaAction(currentStatic, url, {
            closeServiceUrl: window.location.pathname,
          }),
        );
      } else {
        if (cardType === CardCtaTypes.BANK_CARDS) {
          currentStatic.isDisabled = actionsArray[index].value.includes('نیست');
          currentStatic.action.payload = {
            cardNumber: card.card.mainValue.value.replaceAll(/\s/g, ''),
          };
        }
        arrayToBe.push(currentStatic);
      }
    });
    for (let i = fromServerPart.length; i < defaultCtaArray.length; i++) {
      arrayToBe.push(CardCtaItemMapper[defaultCtaArray[i]]);
    }
    this.cardCtaItems.set({ cardsType: cardType, actions: arrayToBe });
  }

  manipulateCtaAction(ctaItem: CardCtaItems, url: string, params: RedirectPayload['params']): CardCtaItems {
    return {
      ...ctaItem,
      action: {
        type: ActionType.REDIRECT,
        payload: {
          url,
          params,
        },
      } as Action,
    };
  }

  extractFromObject(obj: any): any[] {
    const actions: any[] = [];
    if (obj) {
      Object.values(obj).forEach((entry: any) => {
        if (entry.action) {
          actions.push(entry.action);
        }
        if (entry.leftAction) {
          actions.push(entry.leftAction);
        }
        if (entry.rightAction) {
          actions.push(entry.rightAction);
        }
      });
    }
    return actions;
  }
}
